import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AppLink } from './AppLink';

const NAV_LINKS = [
  { label: 'Produit', href: '/product' },
  { label: 'Solutions', href: '/solutions' },
  { label: 'Tarifs', href: '/pricing' },
  { label: 'Clients', href: '/customers' },
  { label: 'Sécurité', href: '/security' },
  { label: 'Ressources', href: '/resources' },
];

const HEADING = { fontFamily: "'Space Grotesk', system-ui, sans-serif" } as const;

export function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMobile = () => setMobileOpen(false);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-[#4f46e5] rounded-xl flex items-center justify-center shadow-[0_0_24px_rgba(79,70,229,0.45)] group-hover:shadow-[0_0_32px_rgba(79,70,229,0.7)] transition-shadow">
              <div className="w-4 h-4 border-2 border-white rotate-45" />
            </div>
            <span className="text-xl font-bold tracking-tight text-white" style={HEADING}>
              Lapyno
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'text-sm font-medium transition-colors',
                    active ? 'text-white' : 'text-slate-400 hover:text-white'
                  )}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <AppLink
              href="https://app.lapyno.com/"
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Connexion
            </AppLink>
            <AppLink
              href="https://app.lapyno.com/?mode=signup"
              className="bg-[#4f46e5] hover:bg-[#4338ca] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-all shadow-[0_0_24px_rgba(79,70,229,0.35)] hover:shadow-[0_0_36px_rgba(79,70,229,0.55)]"
            >
              Essai gratuit
            </AppLink>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-white hover:bg-white/5"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-white/5 animate-fade-in">
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={closeMobile}
                  className="px-4 py-3 text-left text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4 px-4">
                <AppLink
                  href="https://app.lapyno.com/"
                  className="w-full text-center px-5 py-3 rounded-xl border border-white/10 text-white text-sm font-medium hover:bg-white/5"
                >
                  Connexion
                </AppLink>
                <AppLink
                  href="https://app.lapyno.com/?mode=signup"
                  className="w-full text-center px-5 py-3 rounded-xl bg-[#4f46e5] hover:bg-[#4338ca] text-white text-sm font-bold shadow-[0_0_24px_rgba(79,70,229,0.35)]"
                >
                  Essai gratuit
                </AppLink>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
